import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";

export interface EmployerAdmin {
  user_id: string;
  company_name: string | null;
  city: string | null;
  sector: string | null;
  verification_status: string | null;
  is_blocked: boolean;
  trust_score: number | null;
  created_at: string;
  jobs_count: number;
  email: string | null;
  website_url: string | null;
  business_registration_number: string | null;
  verification_notes: string | null;
  verification_checked_at: string | null;
  verification_checked_by: string | null;
}

export const useIsAdmin = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["is-admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Error checking admin status:", error);
        return false;
      }

      return !!data;
    },
    enabled: !!user?.id,
  });
};

export const useAllEmployers = () => {
  const { data: isAdmin } = useIsAdmin();

  return useQuery({
    queryKey: ["admin-employers"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_all_employers_admin");

      if (error) throw error;
      return (data || []) as EmployerAdmin[];
    },
    enabled: isAdmin === true,
  });
};

export const useUpdateEmployerStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      employerId,
      verificationStatus,
      isBlocked,
      verificationNotes,
    }: {
      employerId: string;
      verificationStatus?: string;
      isBlocked?: boolean;
      verificationNotes?: string;
    }) => {
      const { data, error } = await supabase.rpc("update_employer_status_admin", {
        p_employer_id: employerId,
        p_verification_status: verificationStatus || null,
        p_is_blocked: isBlocked ?? null,
        p_verification_notes: verificationNotes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-employers"] });
      toast({
        title: "Statut mis à jour",
        description: "Le statut de l'entreprise a été modifié.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour le statut.",
        variant: "destructive",
      });
    },
  });
};
